import { router, usePage } from "@inertiajs/react"
import { Combobox } from "@/components/ui/combobox"
import type { PageProps } from "@/types/inertia"

/**
 * The "Viewing:" control. It lives in the right-hand slot of a module's sub-nav
 * tab strip rather than in the sidebar — scope belongs to the module that is
 * scoped, not to every screen in the app.
 *
 * District Council staff see their own DC as plain text: their scope is set by
 * their Duo account. IUPAT staff get a type-to-filter dropdown over IUPAT, every
 * District Council, and the orphaned Locals that hang directly off IUPAT.
 */
export function OrgSwitcher() {
  const { props } = usePage<PageProps>()
  const viewing = props.viewing
  const canSwitch = props.can_switch_scope
  const options = (props.switchable_organizations ?? []).map((org) => ({
    value: String(org.id),
    label: org.name,
  }))

  if (!viewing) return null

  // Built on the server: what this scope covers depends on the hierarchy, and
  // for IUPAT it is the whole union rather than a count of anything.
  const localsLabel = viewing.scope_summary

  return (
    <div className="flex min-w-0 items-center gap-2">
      <span className="shrink-0 text-[10px] uppercase tracking-wider text-ink-muted">
        Viewing
      </span>

      {canSwitch ? (
        <Combobox
          options={options}
          value={String(viewing.id)}
          onChange={(value) =>
            router.patch(
              "/scope",
              { organization_id: value },
              { preserveScroll: true },
            )
          }
          triggerLabel="Change which organization you are viewing"
          searchPlaceholder="Filter organizations…"
          emptyMessage="No organization matches."
          align="end"
          className="h-9 min-w-0 py-1.5 text-sm sm:min-w-[13rem]"
        />
      ) : (
        <span className="truncate font-display text-sm font-semibold text-ink-display">
          {viewing.name}
        </span>
      )}

      <span className="hidden shrink-0 whitespace-nowrap text-xs text-ink-muted lg:inline">
        {localsLabel}
      </span>
    </div>
  )
}
